/**
 * Shared palette → theme expansion used by docs-generator and api-docs.
 * Keep this file in sync with infobip-docs-generator/config/expandPalette.js.
 */

const NONE = 'none';
const TRANSPARENT = 'transparent';

/**
 * @typedef {object} PartnerPalette
 * @property {string} primary
 * @property {string} [secondary]
 * @property {string} [tertiary]
 * @property {string} [accent]
 * @property {string} [surface]
 * @property {string} [tint]
 * @property {string} [onPrimary]
 * @property {string} [text]
 * @property {string} [linkSecondary]
 * @property {string} [footer]
 * @property {string} [navbar]
 * @property {string} [sidebarActive]
 */

/**
 * Expand a small partner palette into theme.colorScheme + common component overrides.
 * Explicit `theme` values in config still win when merged afterward.
 *
 * @param {PartnerPalette} palette
 */
function expandPalette(palette) {
	const primary = palette.primary;
	const secondary = palette.secondary ?? primary;
	const tertiary = palette.tertiary ?? '#4c4c4c';
	const accent = palette.accent ?? primary;
	const surface = palette.surface ?? '#f8f9fd';
	const tint = palette.tint ?? '#FFFFFF';
	const onPrimary = palette.onPrimary ?? '#FFFFFF';
	const text = palette.text ?? '#15132B';
	const linkSecondary = palette.linkSecondary ?? tertiary;
	const footerBg = palette.footer ?? surface;
	const navbarBg = palette.navbar ?? surface;
	const footerText = palette.text ?? '#424B5C';
	const sidebarActive = palette.sidebarActive ?? accent;

	const buttonBackground = {
		default: primary,
		hover: accent,
		active: primary,
	};

	const buttonText = {
		default: onPrimary,
		hover: onPrimary,
		active: onPrimary,
	};

	return {
		colorScheme: {
			brand: {
				primary,
				secondary,
				tertiary,
				quaternary: accent,
			},
			lights: {
				primary: tint,
				secondary: surface,
				octinary: surface,
			},
		},
		components: {
			anchors: {
				primary: {
					text: { default: linkSecondary, hover: primary, active: primary },
					background: { default: NONE, hover: NONE, active: NONE },
					secondary: {
						text: { default: primary, hover: primary, active: primary },
						background: { default: '#ffffff', hover: accent, active: accent },
					},
				},
				primaryNavbar: {
					text: { default: text, hover: primary, active: onPrimary },
					background: { default: NONE, hover: NONE, active: primary },
				},
				primaryAsButton: {
					text: buttonText,
					background: buttonBackground,
				},
				secondaryAsButton: {
					text: buttonText,
					background: buttonBackground,
				},
				secondaryNavbar: {
					text: { default: primary, hover: accent, active: primary },
					background: { default: TRANSPARENT, hover: TRANSPARENT, active: TRANSPARENT },
				},
			},
			navbar: {
				backgroundColor: navbarBg,
				primary: {
					text: { default: primary, hover: accent, active: primary },
					background: { active: linkSecondary },
				},
				secondaryNavbar: {
					text: { default: primary, hover: accent, active: primary },
					background: { default: TRANSPARENT, hover: TRANSPARENT, active: TRANSPARENT },
				},
			},
			buttons: {
				primary: {
					text: { default: onPrimary, hover: onPrimary, active: onPrimary },
					background: { default: primary, hover: primary, active: tertiary },
				},
				secondary: {
					text: { default: onPrimary, hover: onPrimary, active: onPrimary },
					background: { default: primary, hover: primary, active: tertiary },
				},
			},
            footer: {
                primary: {
                    text: { default: footerText, hover: primary, active: primary },
                    background: { default: footerBg },
                },
            },
			sidebar: {
				colorIcon: primary,
				background: {
					active: sidebarActive,
					hover: accent,
				},
			},
		},
	};
}

function isPlainObject(value) {
	return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function deepMergePlainObjects(base, override) {
	const result = { ...base };

	for (const [key, value] of Object.entries(override)) {
		const existing = base[key];

		if (isPlainObject(existing) && isPlainObject(value)) {
			result[key] = deepMergePlainObjects(existing, value);
		} else {
			result[key] = value;
		}
	}

	return result;
}

/** Deep-merge theme partials; arrays and primitives from later args win. */
function mergeThemePartials(...partials) {
	return partials.reduce((acc, partial) => {
		if (!partial) {
			return acc;
		}

		const result = { ...acc };

		if (partial.colorScheme) {
			result.colorScheme = {
				...acc.colorScheme,
				brand: { ...acc.colorScheme?.brand, ...partial.colorScheme.brand },
				lights: { ...acc.colorScheme?.lights, ...partial.colorScheme.lights },
			};
		}

		if (partial.components) {
			const mergedComponents = { ...acc.components };

			for (const [componentKey, componentValue] of Object.entries(partial.components)) {
				const existing = mergedComponents[componentKey];

				if (isPlainObject(existing) && isPlainObject(componentValue)) {
					mergedComponents[componentKey] = deepMergePlainObjects(existing, componentValue);
				} else {
					mergedComponents[componentKey] = componentValue;
				}
			}

			result.components = mergedComponents;
		}

		if (partial.sizes) {
			result.sizes = deepMergePlainObjects(acc.sizes ?? {}, partial.sizes);
		}

		return result;
	}, {});
}

/**
 * @param {{ palette?: PartnerPalette, theme?: object, colors?: { lights?: object } }} [config]
 */
function resolveThemeFromConfig(config) {
	const legacyLights = config?.colors?.lights;

	if (!config?.palette && !config?.theme && !legacyLights) {
		return undefined;
	}

	const fromPalette = config.palette ? expandPalette(config.palette) : undefined;
	const fromLegacyColors = legacyLights ? { colorScheme: { lights: legacyLights } } : undefined;

	return mergeThemePartials(fromPalette, fromLegacyColors, config.theme);
}

module.exports = {
	expandPalette,
	mergeThemePartials,
	resolveThemeFromConfig,
};
